import { useFormContext } from 'react-hook-form';
import FormSelectField from '../../../formFields/FormSelectField';
import FormTextAreaField from '../../../formFields/FormTextAreaField';
import { useMemo } from 'react';
import { Row } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  environmentalHazardsOptions,
  otherHazardsOptions,
  physicalHazardsOptions,
} from './safetyPlanOptions';

const SafetyPlanHazardsSection = () => {
  const { register, watch, setValue, getValues } = useFormContext();

  const customEnvironmentalHazard = watch('customEnvironmentalHazard');
  const customOtherHazard = watch('customOtherHazard');

  const addCustomEnvironmentalHazard = () => {
    const customValue = customEnvironmentalHazard?.trim();
    if (customValue) {
      const currentHazards = getValues('environmentalHazards') || [];
      if (!currentHazards.includes(customValue)) {
        setValue('environmentalHazards', [...currentHazards, customValue]);
      }
      setValue('customEnvironmentalHazard', '');
    }
  };

  const addCustomOtherHazard = () => {
    const customValue = customOtherHazard?.trim();
    if (customValue) {
      const currentHazards = getValues('otherHazards') || [];
      if (!currentHazards.includes(customValue)) {
        setValue('otherHazards', [...currentHazards, customValue]);
      }
      setValue('customOtherHazard', '');
    }
  };

  const environmentalHazardsOptionsWithCustom = useMemo(() => {
    const currentEnvironmentalHazards = watch('environmentalHazards') || [];
    const baseOptions = environmentalHazardsOptions.map((hazard) => ({
      label: hazard,
      value: hazard,
    }));

    const customOptions = new Set<string>();
    if (customEnvironmentalHazard && customEnvironmentalHazard.trim()) {
      customOptions.add(customEnvironmentalHazard);
    }

    currentEnvironmentalHazards.forEach((hazard: string) => {
      if (!environmentalHazardsOptions.includes(hazard)) {
        customOptions.add(hazard);
      }
    });

    const customOptionObjects = Array.from(customOptions).map((hazard) => ({
      label: hazard,
      value: hazard,
    }));

    return [...baseOptions, ...customOptionObjects];
  }, [customEnvironmentalHazard, watch]);

  const otherHazardsOptionsWithCustom = useMemo(() => {
    const currentOtherHazards = watch('otherHazards') || [];
    const baseOptions = otherHazardsOptions.map((hazard) => ({
      label: hazard,
      value: hazard,
    }));

    const customOptions = new Set<string>();
    if (customOtherHazard && customOtherHazard.trim()) {
      customOptions.add(customOtherHazard);
    }

    currentOtherHazards.forEach((hazard: string) => {
      if (!otherHazardsOptions.includes(hazard)) {
        customOptions.add(hazard);
      }
    });

    const customOptionObjects = Array.from(customOptions).map((hazard) => ({
      label: hazard,
      value: hazard,
    }));

    return [...baseOptions, ...customOptionObjects];
  }, [customOtherHazard, watch]);

  return (
    <div className="mb-3">
      <div className="mb-3">
        <FormSelectField
          title="Physical Hazards"
          fieldName="physicalHazards"
          placeholder="Click to select physical hazards..."
          options={physicalHazardsOptions.map((hazard) => ({
            label: hazard,
            value: hazard,
          }))}
          isMulti={true}
          isClearable={true}
          variant="full"
        />
      </div>

      <div className="mb-3">
        <FormSelectField
          title="Environmental Hazards"
          fieldName="environmentalHazards"
          placeholder="Click to select environmental hazards..."
          options={environmentalHazardsOptionsWithCustom}
          isMulti={true}
          isClearable={true}
          variant="full"
        />
        <div className="mt-2 d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Add custom environmental hazard..."
            {...register('customEnvironmentalHazard')}
          />
          <StylishNewButton
            type="button"
            className="button--secondary flex-shrink-0"
            onClick={addCustomEnvironmentalHazard}
            disabled={!customEnvironmentalHazard?.trim()}
          >
            Add
          </StylishNewButton>
        </div>
      </div>

      <div className="mb-3">
        <FormSelectField
          title="Other Hazards"
          fieldName="otherHazards"
          placeholder="Click to select other hazards..."
          options={otherHazardsOptionsWithCustom}
          isMulti={true}
          isClearable={true}
          variant="full"
        />
        <div className="mt-2 d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Add custom other hazard..."
            {...register('customOtherHazard')}
          />
          <StylishNewButton
            type="button"
            className="button--secondary flex-shrink-0"
            onClick={addCustomOtherHazard}
            disabled={!customOtherHazard?.trim()}
          >
            Add
          </StylishNewButton>
        </div>
      </div>

      <Row>
        <FormTextAreaField
          title="Other"
          fieldName="otherHazardsText"
          variant="full"
          placeholder="Additional hazard information..."
        />
      </Row>
    </div>
  );
};

export default SafetyPlanHazardsSection;
